// Admin escape-hatch editing of client-submitted cases.
//
// The client is locked out of a case once they submit it (Fix 15: only `draft`
// and `denied` are client-editable). Admins, though, need to fix a submitted
// LIC case before it goes live. This module is the single source of truth for
// "which statuses may an admin still edit" and "which client routes may an
// admin render to do it", so the editor, its deliverable sub-routes, and
// PortalGate all agree.

import type { CompetitionStatus } from "../types/database";
import type { AppRole } from "./auth";
import { isAdminRole } from "./auth";

/**
 * Statuses where the client is locked out post-submit but an admin may still
 * fix the case (before/around approval, before it opens to students).
 */
export const ADMIN_EDIT_STATUSES: CompetitionStatus[] = [
  "submitted",
  "under_review",
  "approved",
];

/** The default client rule: editable only while a draft or after denial. */
export function isClientEditableStatus(status: CompetitionStatus): boolean {
  return status === "draft" || status === "denied";
}

/**
 * Can the current actor edit a case in this status? Admins get the extra
 * post-submit window; everyone else follows the client rule. This is a UI gate
 * — the database (is_admin() RLS on competitions/deliverables + the rubric RPC)
 * is the real authority and already permits these admin writes.
 */
export function caseEditableFor(
  status: CompetitionStatus,
  role: AppRole | null | undefined,
): boolean {
  if (isClientEditableStatus(status)) return true;
  return isAdminRole(role) && ADMIN_EDIT_STATUSES.includes(status);
}

/** True when an admin is editing post-submit (drives the audit + admin chrome). */
export function isAdminPostSubmitEdit(
  status: CompetitionStatus,
  role: AppRole | null | undefined,
): boolean {
  return isAdminRole(role) && !isClientEditableStatus(status) && ADMIN_EDIT_STATUSES.includes(status);
}

/**
 * The client case-editor surface an admin is allowed to render (the editor
 * itself + its deliverable add/edit sub-routes). PortalGate keys off this to
 * admit an admin onto just these client paths — nothing else in the portal.
 */
export function isClientCaseEditPath(pathname: string): boolean {
  return /^\/client\/competition\/[^/]+\/(edit|deliverable)(\/|$)/.test(pathname);
}
